import { getWindow } from "../../effects-bridge";

/**
 * Uplifted pieces of `utils/nativeHooks` (apps/web 140 lines, apps/mobile 101) — SHARED half.
 *
 * Both apps have this file, so under the ledger's standing rule 4 this folder carries all three
 * files. Seeded with the symbols the offline data layer needs, not the whole file.
 *
 * What is here is what is BYTE-IDENTICAL in both apps (modulo quote style): the private `guid`
 * generator, the `callbackObj` registry, and `sendDataToApp`.
 *
 * ⚠️ **`callbackObj` is SHARED MUTABLE STATE and there must be exactly ONE per bundle.** It is a
 * correlation table: `exec`/`execMobileServer` write `callbackObj[id] = { callback }` before
 * posting to the native layer, and `OfflineUtil.handleGetCallback` /
 * `handleLocalServerCallBack` read it back when the native layer answers. A second copy would
 * make every native callback silently no-op. Both apps re-export THIS binding rather than
 * declaring their own, which is what keeps it single.
 */

/**
 * RFC4122-ish correlation id for a native call. Identical in both apps.
 *
 * Not exported by either app file — it is private there — so it is deliberately NOT re-exported
 * by the shims; it is exported here only so the two platform variants (and mobile's app file,
 * whose `exec`/`execMobileServer` stay put) can share one definition instead of two.
 */
export const guid = (): string => {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return (
    s4() +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    s4() +
    s4()
  );
};

/**
 * Pending native callbacks, keyed by the `guid()` handed to the native layer.
 *
 * `let`, not `const`, verbatim from both apps — nothing reassigns it (only property writes,
 * verified by grep), but the binding is preserved as declared.
 */
export let callbackObj: Record<string, any> = {};

/**
 * Post a JSON message to the hosting React Native WebView, if there is one.
 *
 * **Byte-identical in both apps** (web `nativeHooks.js:26`, mobile `:45`) — so it is shared, even
 * though only web's `saga/itemStatus` calls it today.
 *
 * ⚠️ `window` becomes `getWindow()` from the effects bridge, because `packages/core` has no `DOM`
 * lib and must stay RN-safe. The semantics are preserved on both platforms: web wires `window`,
 * mobile wires `globalThis`, and the `?.ReactNativeWebView` guard means a host without a WebView
 * bridge is a silent no-op exactly as before. In bare React Native there is no
 * `ReactNativeWebView`, so this no-ops there — which is what mobile's own copy did too.
 */
export const sendDataToApp = (data: any): void => {
  const payload = JSON.stringify(data);
  const win = getWindow();
  if (win?.ReactNativeWebView) {
    win.ReactNativeWebView.postMessage(payload);
  }
};
